import React from 'react';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next, useTranslation, withTranslation, Trans } from 'react-i18next';

/**
 * The default language translations to load.
 *
 * @see https://github.com/Unibuddy/intl/blob/8231ee1bd2681bd106aab378a584ef8eb74e11da/src/engines/I18nextEngine.ts#L23
 */
var DEFAULT_LOCALE = 'en-GB';
/**
 * Used by I18nextEngine's http-backend service to pull `common.json` when it fires the initial load request.
 * Possibility that apps don't have `common.json` and is absolutely fine. Apps can choose to provide the default namespace.
 *
 * @see https://github.com/Unibuddy/intl/blob/8231ee1bd2681bd106aab378a584ef8eb74e11da/src/engines/I18nextEngine.ts#L24
 */

var DEFAULT_NAMESPACE = 'common';
/**
 * This is the pseudo language used for incontext editing.
 * @see https://support.crowdin.com/enterprise/in-context-setup/
 */

var INCONTEXT_LOCALE = 'ach-UG';
/**
 * *_APP_NAME: These app names are the folder names in S3 bucket where corresponding translations are stored.
 * Also, the name of respective app's Translations repo https://github.com/Unibuddy/translations-{appName}
 */

var WIDGET_APP_NAME = 'widget';
var AMBASSADOR_DASHBOARD_APP_NAME = 'ambassador-dashboard';
var DISCOVER_APP_NAME = 'discover';
var UCAS_APP_NAME = 'ucas';
var LIVE_EVENTS_APP_NAME = 'live-events';
var UNIVERSITY_DASHBOARD_APP_NAME = 'university-dashboard';
var POPCARD_APP_NAME = 'popcard';
var EMAIL_TEMPLATES_APP_NAME = 'email-templates';
var NOTIFICATIONS_APP_NAME = 'notifications';
var CHAT_BUBBLE_APP_NAME = 'chat-bubble';
/**
 * App names Enum for type safety
 */

var APP_NAMES;

(function (APP_NAMES) {
  APP_NAMES["WIDGET"] = "widget";
  APP_NAMES["AMBASSADOR_DASHBOARD"] = "ambassador-dashboard";
  APP_NAMES["DISCOVER"] = "discover";
  APP_NAMES["UCAS"] = "ucas";
  APP_NAMES["LIVE_EVENTS"] = "live-events";
  APP_NAMES["UNIVERSITY_DASHBOARD"] = "university-dashboard";
  APP_NAMES["POPCARD"] = "popcard";
  APP_NAMES["EMAIL_TEMPLATES"] = "email-templates";
  APP_NAMES["NOTIFICATIONS"] = "notifications";
  APP_NAMES["CHAT_BUBBLE"] = "chat-bubble";
})(APP_NAMES || (APP_NAMES = {}));
/**
 * *_PROJECT_NAME: These are projects created for translation in Crowdin system.
 */


var WIDGET_PROJECT_NAME = 'ub-widget';
var POPCARD_PROJECT_NAME = 'ub-popcard';
var AMBASSADOR_DASHBOARD_PROJECT_NAME = 'ub-ambassador-dashboard';
var EMAIL_TEMPLATES_PROJECT_NAME = 'ub-email-templates';
var NOTIFICATIONS_PROJECT_NAME = 'ub-notifications';
var CHAT_BUBBLE_PROJECT_NAME = 'ub-chat-bubble';
var DISCOVER_PROJECT_NAME = 'ub-discover';
/**
 * Project names Enum for type safety
 */

var CROWDIN_PROJECT_NAMES;

(function (CROWDIN_PROJECT_NAMES) {
  CROWDIN_PROJECT_NAMES["WIDGET"] = "ub-widget";
  CROWDIN_PROJECT_NAMES["POPCARD"] = "ub-popcard";
  CROWDIN_PROJECT_NAMES["AMBASSADOR_DASHBOARD"] = "ub-ambassador-dashboard";
  CROWDIN_PROJECT_NAMES["EMAIL_TEMPLATES"] = "ub-email-templates";
  CROWDIN_PROJECT_NAMES["NOTIFICATIONS"] = "ub-notifications";
  CROWDIN_PROJECT_NAMES["CHAT_BUBBLE"] = "ub-chat-bubble";
  CROWDIN_PROJECT_NAMES["DISCOVER"] = "ub-discover";
})(CROWDIN_PROJECT_NAMES || (CROWDIN_PROJECT_NAMES = {}));

var constants = {
  __proto__: null,
  DEFAULT_LOCALE: DEFAULT_LOCALE,
  DEFAULT_NAMESPACE: DEFAULT_NAMESPACE,
  INCONTEXT_LOCALE: INCONTEXT_LOCALE,
  WIDGET_APP_NAME: WIDGET_APP_NAME,
  AMBASSADOR_DASHBOARD_APP_NAME: AMBASSADOR_DASHBOARD_APP_NAME,
  DISCOVER_APP_NAME: DISCOVER_APP_NAME,
  UCAS_APP_NAME: UCAS_APP_NAME,
  LIVE_EVENTS_APP_NAME: LIVE_EVENTS_APP_NAME,
  UNIVERSITY_DASHBOARD_APP_NAME: UNIVERSITY_DASHBOARD_APP_NAME,
  POPCARD_APP_NAME: POPCARD_APP_NAME,
  EMAIL_TEMPLATES_APP_NAME: EMAIL_TEMPLATES_APP_NAME,
  NOTIFICATIONS_APP_NAME: NOTIFICATIONS_APP_NAME,
  CHAT_BUBBLE_APP_NAME: CHAT_BUBBLE_APP_NAME,
  get APP_NAMES () { return APP_NAMES; },
  WIDGET_PROJECT_NAME: WIDGET_PROJECT_NAME,
  POPCARD_PROJECT_NAME: POPCARD_PROJECT_NAME,
  AMBASSADOR_DASHBOARD_PROJECT_NAME: AMBASSADOR_DASHBOARD_PROJECT_NAME,
  EMAIL_TEMPLATES_PROJECT_NAME: EMAIL_TEMPLATES_PROJECT_NAME,
  NOTIFICATIONS_PROJECT_NAME: NOTIFICATIONS_PROJECT_NAME,
  CHAT_BUBBLE_PROJECT_NAME: CHAT_BUBBLE_PROJECT_NAME,
  DISCOVER_PROJECT_NAME: DISCOVER_PROJECT_NAME,
  get CROWDIN_PROJECT_NAMES () { return CROWDIN_PROJECT_NAMES; }
};

/**
 * Checks whether `incontext` query param is present in the url.
 * @example https://xxx.xxx.xxx/xxx?incontext=true
 *
 * @param url current window.location.href
 * @returns boolean
 */
function hasInContext(url) {
  if (url === void 0) {
    url = window.location.href;
  }

  try {
    var queryString = new URL(url).search;
    var urlSearchParams = new URLSearchParams(queryString);
    return urlSearchParams.get('incontext') === 'true';
  } catch (e) {
    console.warn('Intl: hasInContext ', e);
    return false;
  }
}
/**
 * Adds crowdin in context editor script url to the dom's script element's src.
 *
 * @param projectName Crowdin translation project name where app specific translations are stored
 * and must be one of `CROWDIN_PROJECT_NAMES` enum
 *
 * @example CROWDIN_PROJECT_NAMES.WIDGET `ub-widget`
 */

function setInContextScripts(projectName) {
  try {
    // add global vars
    var globalWindow = window;
    globalWindow._jipt = [];

    globalWindow._jipt.push(['project', projectName]); // add crowdin script


    var inContextScript = window.document.getElementById('in-context-editor');

    if (inContextScript) {
      inContextScript.src = '//cdn.crowdin.com/jipt/jipt.js';
    } else {
      console.warn('Intl: setInContextScripts: make sure script element with id="in-context-editor" is present in the html');
    }
  } catch (e) {
    console.warn('Intl: setInContextScripts ', e);
  }
}

var LoadingIndicator = function LoadingIndicator() {
  return React.createElement("div", null, "Loading...");
};
/**
 * Core Intl implementation.
 *
 * @uses @interface IProps
 * @param engine - any i18n engine implementing IBaseEngine
 * @param crowdinProjectName - corresponding translation app's project name in crowdin for supporting incontext editing
 * @returns children
 */


var Intl = function Intl(_ref) {
  var engine = _ref.engine,
      crowdinProjectName = _ref.crowdinProjectName,
      children = _ref.children;

  var _React$useState = React.useState(false),
      ready = _React$useState[0],
      setReady = _React$useState[1];

  var initInContextEditing = React.useCallback(function () {
    setInContextScripts(crowdinProjectName); // change to in-context pseudo-language

    i18n.changeLanguage(INCONTEXT_LOCALE, function (err) {
      if (err) {
        console.warn('%cInContextTranslation is not set. There might be a problem initializing the inContext translation.', 'color: red');
      }

      setReady(true);
    });
  }, [crowdinProjectName]);
  React.useEffect(function () {
    engine.init(function (value) {
      if (value.ready && hasInContext()) {
        initInContextEditing();
      } else {
        setReady(true);
      }
    });
  }, [engine, initInContextEditing]);

  if (!ready) {
    return React.createElement(LoadingIndicator, null);
  }

  return React.createElement(React.Suspense, {
    fallback: React.createElement(LoadingIndicator, null)
  }, children);
};

/**
 * I18next Implementation
 *
 * @implements IBaseEngine
 * @constructor IOptions
 */

var I18nextEngine = /*#__PURE__*/function () {
  function I18nextEngine(options) {
    this._appName = options.appName;
    this._translationsUrl = options.translationsUrl;
    this._lng = options.lng || DEFAULT_LOCALE;
    this._defaultNS = options.defaultNS || DEFAULT_NAMESPACE;
  }
  /**
   * Core function used by Intl component to initialize an engine.
   *
   * Initialising the i18next instance.
   * @see https://react.i18next.com/latest/i18next-instance
   *
   * Uses translations-service to initialise i18next instance and subsequently load namespaces
   * @see https://github.com/Unibuddy/translations-service
   *
   * @param callback called with IValue
   */


  var _proto = I18nextEngine.prototype;

  _proto.init = function init(callback) {
    var httpBackend = {
      crossDomain: true,
      loadPath: this._translationsUrl + "/namespaces/" + this._appName + "/{{lng}}/{{ns}}.json",
      parse: function parse(data) {
        return JSON.parse(data);
      }
    };
    /**
     * For full list of configuratin options
     * @see https://www.i18next.com/overview/configuration-options
     */

    i18n.use(HttpApi).use(initReactI18next).init({
      lng: this._lng,
      load: 'currentOnly',
      fallbackLng: 'en-GB',
      ns: this._defaultNS,
      defaultNS: this._defaultNS,
      interpolation: {
        escapeValue: false
      },
      backend: httpBackend,
      react: {
        bindI18n: 'languageChanged',
        useSuspense: true
      },
      debug: process.env.NODE_ENV === 'development',
      parseMissingKeyHandler: function parseMissingKeyHandler(key) {
        return key;
      },
      returnEmptyString: false
    }, function (err) {
      /**
       * Do not throw error in case of any error from i18n. Application should render fine with default locale.
       */
      if (err) {
        console.warn('%cError initializing i18n. Default values will be shown.', 'color: red');
      }

      callback({
        ready: true
      });
    });
  };

  return I18nextEngine;
}();

/**
 * One of the core API from `react-i18next` and exported as is.
 *
 * @see https://react.i18next.com/latest/usetranslation-hook
 */

/**
 * One of the core API from `react-i18next` and exported as is.
 *
 * @see https://react.i18next.com/latest/withtranslation-hoc
 */

/**
 * One of the core API from `react-i18next` and exported as is.
 *
 * @see https://react.i18next.com/latest/trans-component
 */

/**
 * One of the core API from `react-i18next` and exported as is.
 *
 * @see https://react.i18next.com/latest/i18next-instance
 */

export default Intl;
export { constants as Constants, I18nextEngine, Trans, i18n, useTranslation, withTranslation };
//# sourceMappingURL=intl.esm.js.map
